import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, X, Check, ArrowsClockwise, CurrencyDollar, Stop, Percent, Sun, Moon, Note, X as XIcon, Timer, Prohibit, Shield, Trash, PencilSimple, Target, Lock, Lightning, Clock, Share, Copy, LinkSimple, Eye, WarningCircle } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import GeniLoader from "../components/GeniLoader";
import { useSharedProfile } from "../context/SharedProfileContext";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function OpenPositions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Shared profile mode - when viewing another user's shared profile
  const { isSharedProfileView, isReadOnly } = useSharedProfile();
  
  const [trades, setTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTradesError, setOpenTradesError] = useState(false);
  const [closingTradeId, setClosingTradeId] = useState(null);
  const [closingPrice, setClosingPrice] = useState("");
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stoppingTradeId, setStoppingTradeId] = useState(null); // For stop confirmation
  const [showHoldTimeBadge, setShowHoldTimeBadge] = useState(true); // Default to true
  const [stoppingInProgress, setStoppingInProgress] = useState(false);
  const [updatingTradeType, setUpdatingTradeType] = useState(null); // Track which trade is updating
  
  // Share Positions Modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [positionsSharingEnabled, setPositionsSharingEnabled] = useState(false);
  const [sharingUsername, setSharingUsername] = useState(null);
  const [loadingShareSettings, setLoadingShareSettings] = useState(false);
  const [savingShareSettings, setSavingShareSettings] = useState(false);
  
  // Partial execution state (NEW V1)
  const [partialExecuteTrade, setPartialExecuteTrade] = useState(null);
  const [partialPercentage, setPartialPercentage] = useState(null);
  const [partialMarketPrice, setPartialMarketPrice] = useState("");
  const [partialExecuting, setPartialExecuting] = useState(false);
  const [fetchingPartialPrice, setFetchingPartialPrice] = useState(false);
  
  // Trade Details Drawer state
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [drawerNote, setDrawerNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // Cancel trade state
  const [cancelTradeId, setCancelTradeId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Hedge modal state
  const [hedgeTrade, setHedgeTrade] = useState(null);
  const [hedgeValue, setHedgeValue] = useState("");
  const [hedgeNote, setHedgeNote] = useState("");
  const [hedgeSubmitting, setHedgeSubmitting] = useState(false);
  
  // Adjust Stop modal state
  const [adjustStopTrade, setAdjustStopTrade] = useState(null);
  const [newStopLoss, setNewStopLoss] = useState("");
  const [adjustStopSubmitting, setAdjustStopSubmitting] = useState(false);
  
  // Cancel reason options (same as History page)
  const cancelReasonOptions = [
    { value: 'setup_not_triggered', label: 'Setup not triggered' },
    { value: 'no_entry_reach', label: 'Did not reach entry point' },
    { value: 'skipped_execution', label: 'Skipped execution (no clear reason)' }
  ];

  // Cleanup on unmount to prevent navigation bugs
  useEffect(() => {
    return () => {
      setSelectedTrade(null);
      setPartialExecuteTrade(null);
      setClosingTradeId(null);
      setStoppingTradeId(null);
      setHedgeTrade(null);
      setAdjustStopTrade(null);
    };
  }, []);

  useEffect(() => {
    fetchOpenTrades();
    fetchClosedTrades();
    loadHoldTimeBadgeSetting();
  }, []);

  // Load the Hold-Time badge visibility setting from alert settings
  const loadHoldTimeBadgeSetting = () => {
    try {
      const stored = localStorage.getItem('geni_alert_settings');
      if (stored) {
        const settings = JSON.parse(stored);
        // Default to true if not set
        const showBadge = settings.hold_time_drift?.show_badge !== false;
        setShowHoldTimeBadge(showBadge);
      }
    } catch (e) {
      console.error('Error loading hold-time badge setting:', e);
    }
  };

  // Fetch sharing settings when modal opens
  const fetchSharingSettings = async () => {
    setLoadingShareSettings(true);
    try {
      const response = await api.get('/user/profile');
      setPositionsSharingEnabled(response.data?.positions_sharing_enabled || false);
      setSharingUsername(response.data?.username || null);
    } catch (error) {
      console.error('Error fetching sharing settings:', error);
      toast.error('Failed to load sharing settings');
    } finally {
      setLoadingShareSettings(false);
    }
  };

  // Toggle position sharing
  const togglePositionSharing = async (enabled) => {
    setSavingShareSettings(true);
    try {
      await api.put('/user/profile', { positions_sharing_enabled: enabled });
      setPositionsSharingEnabled(enabled);
      toast.success(enabled ? 'Position sharing enabled' : 'Position sharing disabled');
    } catch (error) {
      console.error('Error updating sharing settings:', error);
      toast.error('Failed to update sharing settings');
    } finally {
      setSavingShareSettings(false);
    }
  };

  // Copy share link to clipboard
  const copyShareLink = () => {
    if (!sharingUsername) {
      toast.error('Please set a username in Settings first');
      return;
    }
    const link = `${window.location.origin}/positions/${sharingUsername}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  // Open share link in new tab
  const openShareLink = () => {
    if (!sharingUsername) {
      toast.error('Please set a username in Settings first');
      return;
    }
    const link = `${window.location.origin}/positions/${sharingUsername}`;
    window.open(link, '_blank');
  };

  const fetchOpenTrades = async () => {
    try {
      const response = await api.get('/trades/open');
      setTrades(response.data.trades || []);
      setOpenTradesError(false);
    } catch (error) {
      // Ignore auth errors - ProtectedRoute handles redirect
      if (error.code !== 'NO_AUTH_SESSION') {
        console.error("Error fetching open trades:", error);
        toast.error("Failed to load open positions");
        // Do NOT clear `trades` here - an error means "we don't know", not "zero
        // positions". Wiping it to [] would render "No Open Positions" as if the
        // user truly had none, which is misleading when it's really a load failure.
        setOpenTradesError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedTrades = async () => {
    try {
      const response = await api.get('/trades');
      // Backend returns trades array directly, not { trades: [...] }
      const allTrades = Array.isArray(response.data) ? response.data : (response.data.trades || []);
      // Filter to only closed trades with valid outcomes
      const closed = allTrades.filter(t => 
        t.status === 'closed' && 
        ['win', 'loss', 'breakeven'].includes(t.outcome)
      );
      setClosedTrades(closed);
    } catch (error) {
      console.error("Error fetching closed trades:", error);
    }
  };

  // Compute hold time baselines for each trade type (matching alert logic exactly)
  const holdTimeBaselines = useMemo(() => {
    const getHoldTimeMs = (trade) => {
      const openTimeStr = trade.open_time || trade.created_at;
      const closeTimeStr = trade.closed_at || trade.close_time || trade.updated_at;
      if (!openTimeStr || !closeTimeStr) return null; // Skip invalid trades
      
      const openTime = new Date(openTimeStr);
      const closeTime = new Date(closeTimeStr);
      if (isNaN(openTime.getTime()) || isNaN(closeTime.getTime())) return null; // Skip invalid dates
      
      return closeTime.getTime() - openTime.getTime();
    };

    const calcMedian = (arr) => {
      if (arr.length === 0) return null;
      const sorted = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 !== 0 
        ? sorted[mid] 
        : (sorted[mid - 1] + sorted[mid]) / 2;
    };

    const computeBaseline = (tradeType) => {
      const typeTrades = closedTrades.filter(t => 
        t.trade_type === tradeType &&
        (t.open_time || t.created_at) &&
        (t.closed_at || t.close_time || t.updated_at)
      );
      
      const wins = typeTrades.filter(t => t.outcome === 'win');
      const losses = typeTrades.filter(t => t.outcome === 'loss');
      
      // Sample size gate: need at least 5 wins AND 5 losses
      if (wins.length < 5 || losses.length < 5) return null;
      
      const winHoldTimes = wins.map(t => getHoldTimeMs(t)).filter(h => h !== null && h > 0);
      const lossHoldTimes = losses.map(t => getHoldTimeMs(t)).filter(h => h !== null && h > 0);
      
      const winBaseline = calcMedian(winHoldTimes);
      const lossBaseline = calcMedian(lossHoldTimes);
      
      if (!winBaseline || !lossBaseline) return null;
      
      // Validity gate: loss baseline must be >= 1.2x win baseline
      if (lossBaseline < winBaseline * 1.2) return null;
      
      return { winBaseline, lossBaseline };
    };

    return {
      day_trade: computeBaseline('day_trade'),
      swing_trade: computeBaseline('swing_trade')
    };
  }, [closedTrades]);

  // Get hold time status for a specific trade (clean version)
  // Only tracks duration for ACTIVE trades (not pending)
  const getHoldTimeStatus = (trade) => {
    // Skip pending trades - they don't have active duration
    if ((trade.position_status || 'active') === 'pending') {
      return null;
    }
    
    const tradeType = trade.trade_type || 'day_trade';
    const baseline = holdTimeBaselines[tradeType];
    
    if (!baseline) return null;
    
    // Use activated_at for active duration (when position became active)
    // Fallback chain: activated_at -> open_time -> created_at (for backward compat)
    const timeSource = trade.activated_at || trade.open_time || trade.created_at;
    if (!timeSource) return null; // No valid timestamp available
    
    const openTime = new Date(timeSource);
    if (isNaN(openTime.getTime())) return null; // Invalid date
    
    const now = new Date();
    const currentHoldMs = now.getTime() - openTime.getTime();
    
    // Strong state: past loss hold baseline
    if (currentHoldMs >= baseline.lossBaseline) {
      return 'strong';
    }
    
    // Soft state: near win hold baseline (>= 90%)
    if (currentHoldMs >= baseline.winBaseline * 0.9) {
      return 'soft';
    }
    
    return null;
  };

  const handleToggleTradeType = async (trade) => {
    const currentType = trade.trade_type || 'day_trade';
    // Cycle through: scalp -> day_trade -> swing_trade -> scalp
    let newType;
    if (currentType === 'scalp') {
      newType = 'day_trade';
    } else if (currentType === 'day_trade') {
      newType = 'swing_trade';
    } else {
      newType = 'scalp';
    }
    
    setUpdatingTradeType(trade.id);
    try {
      await api.patch(`/trades/${trade.id}/trade-type`, {
        trade_type: newType
      });
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === trade.id ? { ...t, trade_type: newType } : t
      ));
      
      const typeLabels = {
        scalp: 'Scalp',
        day_trade: 'Day Trade',
        swing_trade: 'Swing Trade'
      };
      toast.success(`Changed to ${typeLabels[newType]}`);
    } catch (error) {
      console.error("Error updating trade type:", error);
      toast.error("Failed to update trade type");
    } finally {
      setUpdatingTradeType(null);
    }
  };

  // Trade Details Drawer handlers
  const openTradeDrawer = (trade) => {
    setSelectedTrade(trade);
    setDrawerNote(trade.notes || "");
  };

  const closeTradeDrawer = () => {
    setSelectedTrade(null);
    setDrawerNote("");
  };

  const handleSaveNote = async () => {
    if (!selectedTrade) return;
    
    setSavingNote(true);
    try {
      await api.put(`/trades/${selectedTrade.id}`, {
        notes: drawerNote
      });
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === selectedTrade.id ? { ...t, notes: drawerNote } : t
      ));
      
      // Update selected trade
      setSelectedTrade({ ...selectedTrade, notes: drawerNote });
      
      toast.success("Notes saved");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSavingNote(false);
    }
  };

  // Toggle position status between active and pending
  const handleTogglePositionStatus = async (trade) => {
    const currentStatus = trade.position_status || 'active';
    const newStatus = currentStatus === 'active' ? 'pending' : 'active';
    
    try {
      const response = await api.post(`/trades/${trade.id}/position-status`, {
        position_status: newStatus
      });
      
      // Update local state with new status and activated_at (if returned)
      setTrades(trades.map(t => 
        t.id === trade.id ? { 
          ...t, 
          position_status: newStatus,
          activated_at: response.data.activated_at || t.activated_at
        } : t
      ));
      
      // Update selected trade if it's the same
      if (selectedTrade?.id === trade.id) {
        setSelectedTrade({ 
          ...selectedTrade, 
          position_status: newStatus,
          activated_at: response.data.activated_at || selectedTrade.activated_at
        });
      }
      
      toast.success(`Position marked as ${newStatus.toUpperCase()}`);
    } catch (error) {
      console.error("Error changing position status:", error);
      toast.error(error.response?.data?.detail || "Failed to change position status");
    }
  };

  const handleFetchMarketPrice = async (symbol) => {
    setFetchingPrice(true);
    try {
      const response = await api.get(`/market-price/${symbol}`);
      setClosingPrice(response.data.price.toString());
      toast.success(`Market price: $${response.data.price.toLocaleString()} (${response.data.source})`);
    } catch (error) {
      console.error("Error fetching market price:", error);
      toast.error("Could not fetch market price. Please enter manually.");
    } finally {
      setFetchingPrice(false);
    }
  };

  const handleCloseTrade = async (tradeId) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot close trades in view-only mode");
      return;
    }
    
    if (!closingPrice || parseFloat(closingPrice) <= 0) {
      toast.error("Please enter a valid closing price");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/trades/${tradeId}/close`, {
        closing_price: parseFloat(closingPrice)
      });

      toast.success(
        `Trade closed! ${response.data.outcome.toUpperCase()} - ${
          response.data.profit_usd >= 0 ? '+' : ''
        }$${response.data.profit_usd?.toFixed(2) || '0.00'}`
      );

      // Remove from list
      setTrades(trades.filter(t => t.id !== tradeId));
      setClosingTradeId(null);
      setClosingPrice("");
    } catch (error) {
      console.error("Error closing trade:", error);
      toast.error(error.response?.data?.detail || "Failed to close trade");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStopTrade = async (tradeId) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot stop trades in view-only mode");
      return;
    }
    
    setStoppingInProgress(true);
    try {
      const response = await api.post(`/trades/${tradeId}/stop`);
      const profitUsd = response.data.profit_usd || 0;
      
      // Show actual stop result (could be loss, breakeven, or even profit if stop was moved)
      let resultMessage;
      if (profitUsd > 0) {
        resultMessage = `Trade stopped at SL. PROFIT +$${profitUsd.toFixed(2)}`;
      } else if (profitUsd < 0) {
        resultMessage = `Trade stopped at SL. LOSS -$${Math.abs(profitUsd).toFixed(2)}`;
      } else {
        resultMessage = `Trade stopped at SL. BREAKEVEN $0.00`;
      }
      
      toast.success(resultMessage);

      // Remove from list
      setTrades(trades.filter(t => t.id !== tradeId));
      setStoppingTradeId(null);
    } catch (error) {
      console.error("Error stopping trade:", error);
      toast.error(error.response?.data?.detail || "Failed to stop trade");
    } finally {
      setStoppingInProgress(false);
    }
  };

  const cancelStop = () => {
    setStoppingTradeId(null);
  };

  const openClosePanel = (trade) => {
    setClosingTradeId(trade.id);
    setClosingPrice("");
  };

  const cancelClose = () => {
    setClosingTradeId(null);
    setClosingPrice("");
  };

  // NEW Partial Execution functions (V1)
  const openPartialExecuteModal = (trade) => {
    setPartialExecuteTrade(trade);
    setPartialPercentage(null);
    setPartialMarketPrice("");
  };

  const closePartialExecuteModal = () => {
    setPartialExecuteTrade(null);
    setPartialPercentage(null);
    setPartialMarketPrice("");
    setFetchingPartialPrice(false);
  };

  const handleFetchPartialPrice = async (symbol) => {
    setFetchingPartialPrice(true);
    try {
      const response = await api.get(`/market-price/${symbol}`);
      if (response.data.price) {
        setPartialMarketPrice(response.data.price.toString());
        toast.success(`Market price: $${response.data.price.toLocaleString()} (${response.data.source})`);
      }
    } catch (error) {
      console.error("Error fetching market price:", error);
      toast.error("Could not fetch market price. Please enter manually.");
    } finally {
      setFetchingPartialPrice(false);
    }
  };

  const handlePartialExecute = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot execute partials in view-only mode");
      return;
    }
    
    if (!partialExecuteTrade || !partialPercentage || !partialMarketPrice) {
      toast.error("Please select percentage and enter market price");
      return;
    }

    const price = parseFloat(partialMarketPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("Please enter a valid market price");
      return;
    }

    setPartialExecuting(true);
    try {
      const response = await api.post(`/trades/${partialExecuteTrade.id}/partial-execute`, {
        percentage: partialPercentage,
        market_price: price
      });

      toast.success(response.data.message);

      // Check if the trade was auto-closed (0% remaining)
      if (response.data.auto_closed) {
        // Remove from Open Positions - it's now in History
        setTrades(trades.filter(t => t.id !== partialExecuteTrade.id));
      } else {
        // Update the trade in local state (stays open)
        setTrades(trades.map(t => 
          t.id === partialExecuteTrade.id ? response.data.trade : t
        ));
      }
      
      closePartialExecuteModal();
      
    } catch (error) {
      console.error("Error executing partial:", error);
      toast.error(error.response?.data?.detail || "Failed to execute partial");
    } finally {
      setPartialExecuting(false);
    }
  };

  // Cancel trade handler (same logic as History page)
  const handleCancelTrade = async (id, reason) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot cancel trades in view-only mode");
      return;
    }
    
    try {
      await api.post(`/trades/${id}/cancel?cancel_reason=${reason}`);
      toast.success("Trade marked as not triggered");
      
      // Remove from open positions (it's now in History as canceled)
      setTrades(trades.filter(t => t.id !== id));
      
      setCancelTradeId(null);
      setCancelReason('');
      
      // Close drawer if this trade was selected
      if (selectedTrade?.id === id) {
        setSelectedTrade(null);
      }
    } catch (error) {
      console.error("Error canceling trade:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel trade");
    }
  };

  // Open cancel confirmation modal
  const openCancelModal = (tradeId) => {
    setCancelTradeId(tradeId);
    setCancelReason('');
  };

  // Close cancel confirmation modal
  const closeCancelModal = () => {
    setCancelTradeId(null);
    setCancelReason('');
  };

  // HEDGE FUNCTIONS
  const openHedgeModal = (trade) => {
    setHedgeTrade(trade);
    // Always start with empty fields - user is adding a new hedge
    setHedgeValue("");
    setHedgeNote("");
  };

  const closeHedgeModal = () => {
    setHedgeTrade(null);
    setHedgeValue("");
    setHedgeNote("");
  };

  const handleSaveHedge = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot add hedges in view-only mode");
      return;
    }
    
    if (!hedgeTrade) return;
    
    const value = parseFloat(hedgeValue);
    if (isNaN(value)) {
      toast.error("Please enter a valid hedge value");
      return;
    }

    setHedgeSubmitting(true);
    try {
      const response = await api.post(`/trades/${hedgeTrade.id}/hedge`, {
        hedge_value: value,
        hedge_note: hedgeNote || null
      });

      toast.success(response.data.message);
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === hedgeTrade.id ? response.data.trade : t
      ));
      
      closeHedgeModal();
    } catch (error) {
      console.error("Error saving hedge:", error);
      toast.error(error.response?.data?.detail || "Failed to save hedge");
    } finally {
      setHedgeSubmitting(false);
    }
  };

  const handleRemoveHedge = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot remove hedges in view-only mode");
      return;
    }
    
    if (!hedgeTrade) return;

    setHedgeSubmitting(true);
    try {
      const response = await api.delete(`/trades/${hedgeTrade.id}/hedge`);

      toast.success(response.data.message);
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === hedgeTrade.id ? response.data.trade : t
      ));
      
      closeHedgeModal();
    } catch (error) {
      console.error("Error removing hedge:", error);
      toast.error(error.response?.data?.detail || "Failed to remove hedge");
    } finally {
      setHedgeSubmitting(false);
    }
  };

  const handleRemoveSingleHedge = async (hedgeEntryId) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot remove hedges in view-only mode");
      return;
    }
    
    if (!hedgeTrade || !hedgeEntryId) return;

    setHedgeSubmitting(true);
    try {
      const response = await api.delete(`/trades/${hedgeTrade.id}/hedge/${hedgeEntryId}`);

      toast.success(response.data.message);
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === hedgeTrade.id ? response.data.trade : t
      ));
      
      // Update the hedgeTrade in the modal to reflect changes
      setHedgeTrade(response.data.trade);
    } catch (error) {
      console.error("Error removing hedge entry:", error);
      toast.error(error.response?.data?.detail || "Failed to remove hedge entry");
    } finally {
      setHedgeSubmitting(false);
    }
  };

  // ADJUST STOP FUNCTIONS
  const openAdjustStopModal = (trade) => {
    setAdjustStopTrade(trade);
    setNewStopLoss(trade.stop_loss?.toString() || "");
  };

  const closeAdjustStopModal = () => {
    setAdjustStopTrade(null);
    setNewStopLoss("");
  };

  const calculateRiskPreview = (trade, newStop) => {
    if (!trade || !newStop) return null;
    
    const entry = trade.entry;
    const direction = trade.direction;
    const initialStop = trade.initial_stop_loss || trade.stop_loss;
    const initialRisk = trade.initial_risk_amount || trade.hand_size || 0;
    const remainingPct = trade.remaining_position_pct ?? 100;  // Account for partials
    const newStopValue = parseFloat(newStop);
    
    if (isNaN(newStopValue) || newStopValue <= 0 || initialRisk === 0) return null;
    
    const initialStopDistance = Math.abs(entry - initialStop);
    const newStopDistance = Math.abs(entry - newStopValue);
    
    if (initialStopDistance === 0) return null;
    
    // Check if this is a profit-locking stop (beyond entry)
    const isProfitLocking = (direction === 'long' && newStopValue > entry) || 
                            (direction === 'short' && newStopValue < entry);
    
    // Check if this is a break-even stop
    const isBreakEven = newStopValue === entry;
    
    // Calculate current risk/reward based on stop position
    let currentRisk;
    if (isProfitLocking) {
      // Stop is beyond entry - this locks in profit (negative risk = guaranteed profit)
      currentRisk = -(remainingPct / 100) * initialRisk * (newStopDistance / initialStopDistance);
    } else if (isBreakEven) {
      currentRisk = 0;
    } else {
      // Normal protective stop
      currentRisk = (remainingPct / 100) * initialRisk * (newStopDistance / initialStopDistance);
    }
    
    // Compare against current remaining risk (before this adjustment)
    const previousRemainingRisk = (remainingPct / 100) * initialRisk;
    const riskChange = currentRisk - previousRemainingRisk;
    const riskReduced = riskChange < 0;  // Lower or negative = better
    
    return {
      initialRisk: initialRisk,
      remainingPct: remainingPct,
      currentRisk: Math.round(currentRisk * 100) / 100,
      riskChange: Math.round(riskChange * 100) / 100,
      riskReduced: riskReduced,
      percentChange: previousRemainingRisk > 0 ? Math.round((riskChange / previousRemainingRisk) * 100) : 0,
      isProfitLocking: isProfitLocking,
      isBreakEven: isBreakEven
    };
  };

  const handleSaveAdjustStop = async () => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot adjust stop in view-only mode");
      return;
    }
    
    if (!adjustStopTrade) return;
    
    const newStop = parseFloat(newStopLoss);
    if (isNaN(newStop) || newStop <= 0) {
      toast.error("Please enter a valid stop loss price");
      return;
    }

    // NOTE: Directional validation is NOT enforced for stop adjustment.
    // This allows:
    //   - LONG stops above entry (profit-locking)
    //   - SHORT stops below entry (profit-locking)
    //   - Break-even stops
    //   - Trailing stop management
    // Directional validation only applies during NEW TRADE CREATION.
    
    // Only check if stop is meaningfully different
    if (newStop === adjustStopTrade.stop_loss) {
      toast.error("New stop is the same as current stop");
      return;
    }

    setAdjustStopSubmitting(true);
    try {
      const response = await api.post(`/trades/${adjustStopTrade.id}/adjust-stop`, {
        new_stop_loss: newStop
      });

      toast.success(response.data.message);
      
      // Update local state
      setTrades(trades.map(t => 
        t.id === adjustStopTrade.id ? response.data.trade : t
      ));
      
      closeAdjustStopModal();
    } catch (error) {
      console.error("Error adjusting stop:", error);
      toast.error(error.response?.data?.detail || "Failed to adjust stop loss");
    } finally {
      setAdjustStopSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="open-positions-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-black font-heading tracking-tight">Open Positions</h2>
            <p className="text-white/60 mt-1">Manage your active trades</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Share My Positions button - only in private view */}
            {!isSharedProfileView && (
              <Button
                variant="outline"
                size="sm"
                className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                onClick={() => {
                  setShowShareModal(true);
                  fetchSharingSettings();
                }}
                data-testid="share-positions-btn"
              >
                <Share size={16} className="mr-2" />
                Share
              </Button>
            )}
            {/* Active/Pending Breakdown */}
            <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-sm px-3 py-2">
              <div className="flex items-center gap-1.5">
                <Lightning size={14} weight="fill" className="text-[#00E676]" />
                <span className="text-[#00E676] font-bold text-sm">
                  {trades.filter(t => (t.position_status || 'active') === 'active').length}
                </span>
                <span className="text-white/40 text-xs">ACTIVE</span>
              </div>
              <div className="w-px h-4 bg-white/10"></div>
              <div className="flex items-center gap-1.5">
                <Clock size={14} weight="fill" className="text-[#F59E0B]" />
                <span className="text-[#F59E0B] font-bold text-sm">
                  {trades.filter(t => t.position_status === 'pending').length}
                </span>
                <span className="text-white/40 text-xs">PENDING</span>
              </div>
            </div>
            {/* Total */}
            <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
              <span className="text-white/60 text-sm font-mono">TOTAL: </span>
              <span className="text-white font-bold text-lg">{trades.length}</span>
            </div>
          </div>
        </div>

        {/* Positions List */}
        {openTradesError && trades.length === 0 ? (
          <div className="bg-[#121212] border border-[#F59E0B]/30 rounded-sm p-12 text-center" data-testid="open-positions-error-state">
            <div className="w-16 h-16 bg-[#F59E0B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <WarningCircle size={32} weight="duotone" className="text-[#F59E0B]" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">Failed to load open positions</h3>
            <p className="text-white/60 mb-6">
              This may be a temporary connection issue. This does not mean you have no open positions.
            </p>
            <Button
              onClick={() => { setLoading(true); fetchOpenTrades(); }}
              data-testid="retry-open-positions-btn"
              className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-bold"
            >
              <ArrowsClockwise size={16} className="mr-2" />
              Try Again
            </Button>
          </div>
        ) : trades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendUp size={32} weight="duotone" className="text-white/40" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">No Open Positions</h3>
            <p className="text-white/60">
              Analyze a chart on the Dashboard to open a new position.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {openTradesError && (
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid="open-positions-stale-banner">
                <div className="flex items-center gap-2 text-[#F59E0B] text-sm">
                  <WarningCircle size={16} weight="duotone" />
                  Failed to refresh — showing the last loaded positions.
                </div>
                <Button
                  onClick={() => fetchOpenTrades()}
                  data-testid="retry-open-positions-stale-btn"
                  variant="ghost"
                  size="sm"
                  className="text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs"
                >
                  Try Again
                </Button>
              </div>
            )}
            {trades.map((trade) => (
              <div
                key={trade.id}
                data-testid={`position-${trade.id}`}
                className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden cursor-pointer hover:border-white/20 transition-colors"
                onClick={() => openTradeDrawer(trade)}
              >
                {/* Main Row */}
                <div className="p-6 flex items-start justify-between gap-6">
                  {/* Left Section: Asset, Metadata, Metrics */}
                  <div className="flex-1 min-w-0">
                    {/* Top Row: Asset + Metrics */}
                    <div className="flex items-start gap-8">
                      {/* Asset Name */}
                      <div className="flex-shrink-0">
                        <div className="text-2xl font-black font-heading tracking-tight">
                          {trade.asset}
                        </div>
                        
                        {/* Metadata Block - 2-row layout */}
                        <div className="mt-2 space-y-1.5">
                          {/* Row 1: Direction (Primary) */}
                          <div className="flex items-center">
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                              trade.direction === "long" 
                                ? "bg-[#00E676]/10 text-[#00E676]" 
                                : "bg-[#FF3B30]/10 text-[#FF3B30]"
                            }`}>
                              {trade.direction === "long" ? (
                                <TrendUp size={14} weight="bold" />
                              ) : (
                                <TrendDown size={14} weight="bold" />
                              )}
                              {trade.direction.toUpperCase()}
                            </div>
                          </div>
                          
                          {/* Row 2: Trade Type + Setup (Secondary) */}
                          <div className="flex items-center gap-2">
                            {isSharedProfileView ? (
                              /* Shared Profile Mode: Non-clickable trade type badge */
                              <div
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${
                                  trade.trade_type === 'swing_trade'
                                    ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                                    : trade.trade_type === 'scalp'
                                    ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                                    : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                }`}
                                data-testid={`trade-type-badge-${trade.id}`}
                              >
                                {trade.trade_type === 'swing_trade' ? (
                                  <Moon size={10} weight="fill" />
                                ) : trade.trade_type === 'scalp' ? (
                                  <Lightning size={10} weight="fill" />
                                ) : (
                                  <Sun size={10} weight="fill" />
                                )}
                                {trade.trade_type === 'swing_trade' ? 'Swing' : trade.trade_type === 'scalp' ? 'Scalp' : 'Day'}
                              </div>
                            ) : (
                              /* Normal Mode: Clickable to toggle trade type (cycles: scalp → day → swing → scalp) */
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleTradeType(trade); }}
                                disabled={updatingTradeType === trade.id}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all cursor-pointer hover:opacity-80 ${
                                  trade.trade_type === 'swing_trade'
                                    ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                                    : trade.trade_type === 'scalp'
                                    ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                                    : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                }`}
                                data-testid={`trade-type-toggle-${trade.id}`}
                                title="Click to cycle trade type: Scalp → Day → Swing"
                              >
                                {updatingTradeType === trade.id ? (
                                  <ArrowsClockwise size={10} className="animate-spin" />
                                ) : trade.trade_type === 'swing_trade' ? (
                                  <Moon size={10} weight="fill" />
                                ) : trade.trade_type === 'scalp' ? (
                                  <Lightning size={10} weight="fill" />
                                ) : (
                                  <Sun size={10} weight="fill" />
                                )}
                                {trade.trade_type === 'swing_trade' ? 'Swing' : trade.trade_type === 'scalp' ? 'Scalp' : 'Day'}
                              </button>
                            )}
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#FFC107]/10 text-[#FFC107]/80 border border-[#FFC107]/20">
                              {trade.setup_name || 'No setup'}
                            </div>
                          </div>
                          
                          {/* Row 3: Notes (Tertiary - only if exists or for empty state) */}
                          <div className="flex items-center">
                            {/* Position Status Badge (Active/Pending) */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSharedProfileView) return; // Disable in shared profile mode
                                handleTogglePositionStatus(trade);
                              }}
                              disabled={isSharedProfileView}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium mr-2 transition-colors ${
                                (trade.position_status || 'active') === 'active'
                                  ? "bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/20"
                                  : "bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/20"
                              } ${isSharedProfileView ? 'cursor-default' : 'cursor-pointer'}`}
                              title={(trade.position_status || 'active') === 'active' 
                                ? "Position is ACTIVE - Click to mark as PENDING" 
                                : "Position is PENDING - Click to mark as ACTIVE"}
                              data-testid={`position-status-badge-${trade.id}`}
                            >
                              {(trade.position_status || 'active') === 'active' ? (
                                <>
                                  <Lightning size={10} weight="fill" />
                                  ACTIVE
                                </>
                              ) : (
                                <>
                                  <Clock size={10} weight="fill" />
                                  PENDING
                                </>
                              )}
                            </button>
                            
                            {/* Notes Badge */}
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${
                              trade.notes 
                                ? "bg-white/10 text-white/70 border border-white/20"
                                : "bg-white/5 text-white/30 border border-white/10"
                            }`}>
                              <Note size={10} weight={trade.notes ? "fill" : "regular"} />
                              {trade.notes ? "1 note" : "Notes"}
                            </div>
                            {/* Hold-Time Status Indicator */}
                            {showHoldTimeBadge && (() => {
                              const status = getHoldTimeStatus(trade);
                              if (status === 'strong') {
                                return (
                                  <div 
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#FF453A]/10 text-[#FF453A] border border-[#FF453A]/30 ml-2"
                                    title="This position has exceeded your average losing hold time"
                                    data-testid={`hold-time-strong-${trade.id}`}
                                  >
                                    <Timer size={10} weight="fill" />
                                    Past loss hold
                                  </div>
                                );
                              }
                              if (status === 'soft') {
                                return (
                                  <div 
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#FFD60A]/10 text-[#FFD60A] border border-[#FFD60A]/30 ml-2"
                                    title="Approaching your average winner hold time"
                                    data-testid={`hold-time-soft-${trade.id}`}
                                  >
                                    <Timer size={10} weight="fill" />
                                    Near win hold
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Entry & Levels - Metrics */}
                      <div className="flex gap-6">
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                          <div className="text-white font-mono font-semibold">
                            ${trade.entry?.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1 flex items-center gap-1">
                            STOP LOSS
                            {trade.stop_adjusted && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-[#FFC107]/10 text-[#FFC107] border border-[#FFC107]/20">
                                Adjusted
                              </span>
                            )}
                          </div>
                          {isSharedProfileView ? (
                            /* Shared Profile Mode: Show stop loss without edit functionality */
                            <div 
                              className="text-[#FF3B30] font-mono font-semibold"
                              data-testid={`stop-loss-${trade.id}`}
                            >
                              ${trade.stop_loss?.toLocaleString()}
                            </div>
                          ) : (
                            /* Normal Mode: Clickable to adjust stop */
                            <button
                              onClick={(e) => { e.stopPropagation(); openAdjustStopModal(trade); }}
                              className="text-[#FF3B30] font-mono font-semibold hover:text-[#FF3B30]/80 transition-colors flex items-center gap-1.5 group cursor-pointer"
                              data-testid={`adjust-stop-btn-${trade.id}`}
                              title="Click to adjust stop loss"
                            >
                              ${trade.stop_loss?.toLocaleString()}
                              <PencilSimple size={12} weight="bold" className="text-[#FF3B30]/50 group-hover:text-[#FF3B30] transition-colors" />
                            </button>
                          )}
                          {/* Show initial stop if different */}
                          {trade.stop_adjusted && trade.initial_stop_loss && trade.initial_stop_loss !== trade.stop_loss && (
                            <div className="text-[10px] text-white/30 font-mono mt-0.5">
                              Initial: ${trade.initial_stop_loss?.toLocaleString()}
                            </div>
                          )}
                        </div>
                        {trade.take_profit && (
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">TAKE PROFIT</div>
                            <div className="text-[#00E676] font-mono font-semibold">
                              ${trade.take_profit?.toLocaleString()}
                            </div>
                          </div>
                        )}
                        {trade.hand_size && (
                          <div>
                            <div className="text-white/40 text-xs font-mono mb-1">HAND SIZE</div>
                            {isSharedProfileView ? (
                              <div className="text-white/40 font-mono font-semibold flex items-center gap-1">
                                <Lock size={12} weight="fill" className="text-white/30" />
                                <span>Hidden</span>
                              </div>
                            ) : (
                              <div className="text-white font-mono font-semibold">
                                ${trade.hand_size?.toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Partial Execution Info */}
                        {(trade.remaining_position_pct !== undefined && trade.remaining_position_pct < 100) && (
                          <>
                            <div className="border-l border-white/10 h-auto self-stretch mx-1" />
                            <div>
                              <div className="text-white/40 text-xs font-mono mb-1">REMAINING</div>
                              <div className="text-[#FFC107] font-mono font-semibold">
                                {trade.remaining_position_pct}%
                              </div>
                            </div>
                            <div>
                              <div className="text-white/40 text-xs font-mono mb-1">REALIZED</div>
                              {isSharedProfileView ? (
                                /* Shared Profile Mode: Show only RR, hide USD */
                                <div className="font-mono font-semibold">
                                  {trade.hand_size > 0 ? (
                                    (() => {
                                      const realizedUsd = trade.realized_pnl || 0;
                                      const hedgeVal = trade.hedge_value || 0;
                                      const handSize = trade.hand_size;
                                      const realizedRR = (realizedUsd + hedgeVal) / handSize;
                                      const isPositive = realizedRR > 0;
                                      const isZero = realizedRR === 0;
                                      
                                      return (
                                        <span className={`${
                                          isZero 
                                            ? 'text-white/40' 
                                            : isPositive 
                                              ? 'text-[#00E676]' 
                                              : 'text-[#FF3B30]'
                                        }`}>
                                          {isPositive ? '+' : ''}{realizedRR.toFixed(2)}R
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-white/40 flex items-center gap-1">
                                      <Lock size={12} weight="fill" className="text-white/30" />
                                      Hidden
                                    </span>
                                  )}
                                  {/* Hedge indicator (no $ amount) */}
                                  {trade.has_hedge && trade.hedge_value !== 0 && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Shield size={9} weight="fill" className="text-cyan-400/60" />
                                      <span className="font-mono text-xs text-cyan-400/60">Hedged</span>
                                      {(trade.hedge_count || 1) > 1 && (
                                        <span className="text-[9px] text-cyan-400/40">×{trade.hedge_count}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Normal Mode: Show USD with RR below */
                                <>
                                  <div className={`font-mono font-semibold ${
                                    (trade.realized_pnl || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                                  }`}>
                                    {(trade.realized_pnl || 0) >= 0 ? '+' : ''}${(trade.realized_pnl || 0).toFixed(2)}
                                  </div>
                                  {/* Realized RR - secondary line showing R-multiple efficiency */}
                                  {trade.hand_size > 0 && (
                                    <div className="mt-0.5" data-testid={`realized-rr-${trade.id}`}>
                                      {(() => {
                                        const realizedUsd = trade.realized_pnl || 0;
                                        const hedgeVal = trade.hedge_value || 0;
                                        const handSize = trade.hand_size;
                                        const realizedRR = (realizedUsd + hedgeVal) / handSize;
                                        const isPositive = realizedRR > 0;
                                        const isZero = realizedRR === 0;
                                        
                                        return (
                                          <span className={`font-mono text-xs ${
                                            isZero 
                                              ? 'text-white/40' 
                                              : isPositive 
                                                ? 'text-[#00E676]/80' 
                                                : 'text-[#FF3B30]/80'
                                          }`}>
                                            {isPositive ? '+' : ''}{realizedRR.toFixed(2)}R
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  )}
                                  {/* Hedge below Realized RR - tertiary modifier */}
                                  {trade.has_hedge && trade.hedge_value !== 0 && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Shield size={9} weight="fill" className="text-cyan-400/60" />
                                      <span className={`font-mono text-xs ${
                                        (trade.hedge_value || 0) >= 0 ? 'text-cyan-400/80' : 'text-cyan-400/80'
                                      }`}>
                                        {(trade.hedge_value || 0) >= 0 ? '+' : ''}${(trade.hedge_value || 0).toFixed(2)}
                                      </span>
                                      {(trade.hedge_count || 1) > 1 && (
                                        <span className="text-[9px] text-cyan-400/40">×{trade.hedge_count}</span>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                        {/* Hedge without Partial - show in its own column only when no partial */}
                        {(trade.remaining_position_pct === undefined || trade.remaining_position_pct >= 100) && trade.has_hedge && trade.hedge_value !== 0 && (
                          <>
                            <div className="border-l border-cyan-500/20 h-auto self-stretch mx-1" />
                            <div>
                              <div className="text-cyan-400/60 text-xs font-mono mb-1 flex items-center gap-1">
                                <Shield size={10} weight="fill" />
                                HEDGE
                                {(trade.hedge_count || 1) > 1 && (
                                  <span className="text-[10px] text-cyan-400/40">×{trade.hedge_count}</span>
                                )}
                              </div>
                              {isSharedProfileView ? (
                                /* Shared Profile Mode: Show RR only, hide USD */
                                <div className="font-mono font-semibold">
                                  {trade.hand_size > 0 ? (
                                    (() => {
                                      const realizedUsd = trade.realized_pnl || 0;
                                      const hedgeVal = trade.hedge_value || 0;
                                      const handSize = trade.hand_size;
                                      const realizedRR = (realizedUsd + hedgeVal) / handSize;
                                      const isPositive = realizedRR > 0;
                                      const isZero = realizedRR === 0;
                                      
                                      return (
                                        <span className={`${
                                          isZero 
                                            ? 'text-white/40' 
                                            : isPositive 
                                              ? 'text-cyan-400' 
                                              : 'text-[#FF3B30]'
                                        }`}>
                                          {isPositive ? '+' : ''}{realizedRR.toFixed(2)}R
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-cyan-400/60">Hedged</span>
                                  )}
                                </div>
                              ) : (
                                /* Normal Mode: Show USD with RR */
                                <>
                                  <div className="font-mono font-semibold text-cyan-400">
                                    {(trade.hedge_value || 0) >= 0 ? '+' : ''}${(trade.hedge_value || 0).toFixed(2)}
                                  </div>
                                  {/* Realized RR from hedge only (no partials) */}
                                  {trade.hand_size > 0 && (
                                    <div className="mt-0.5" data-testid={`hedge-only-rr-${trade.id}`}>
                                      {(() => {
                                        const realizedUsd = trade.realized_pnl || 0;
                                        const hedgeVal = trade.hedge_value || 0;
                                        const handSize = trade.hand_size;
                                        const realizedRR = (realizedUsd + hedgeVal) / handSize;
                                        const isPositive = realizedRR > 0;
                                        const isZero = realizedRR === 0;
                                        
                                        return (
                                          <span className={`font-mono text-xs ${
                                            isZero 
                                              ? 'text-white/40' 
                                              : isPositive 
                                                ? 'text-cyan-400/70' 
                                                : 'text-[#FF3B30]/80'
                                          }`}>
                                            {isPositive ? '+' : ''}{realizedRR.toFixed(2)}R
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Section: Actions */}
                  <div className="flex flex-col items-end gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {/* Primary Actions Row */}
                    {closingTradeId !== trade.id && stoppingTradeId !== trade.id && (
                      <div className="flex items-center gap-2">
                        {/* Stop Button - Disabled for pending trades */}
                        <Button
                          onClick={(isSharedProfileView || (trade.position_status || 'active') === 'pending') ? undefined : (e) => { e.stopPropagation(); setStoppingTradeId(trade.id); }}
                          data-testid={`stop-btn-${trade.id}`}
                          className={`bg-[#FF6B6B]/20 text-[#1A1A1A] font-semibold px-3 border border-[#FF6B6B]/30 ${
                            (isSharedProfileView || (trade.position_status || 'active') === 'pending') ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FF6B6B]/30'
                          }`}
                          style={{ backgroundColor: 'rgba(255, 107, 107, 0.15)', color: '#1a1a1a' }}
                          disabled={isSharedProfileView || (trade.position_status || 'active') === 'pending'}
                          title={(trade.position_status || 'active') === 'pending' ? "Mark position as ACTIVE before using Stop" : "Close at stop-loss"}
                        >
                          <Stop size={16} weight="fill" className="mr-1.5 text-[#FF6B6B]" />
                          <span className="text-[#FF6B6B]">Stop</span>
                        </Button>
                        {/* Partial Button - Disabled for pending trades */}
                        <Button
                          onClick={(isSharedProfileView || (trade.position_status || 'active') === 'pending') ? undefined : (e) => { e.stopPropagation(); openPartialExecuteModal(trade); }}
                          data-testid={`partial-btn-${trade.id}`}
                          variant="outline"
                          className={`bg-transparent border-[#FFC107]/30 text-[#FFC107] font-semibold px-3 ${
                            (isSharedProfileView || (trade.position_status || 'active') === 'pending') ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FFC107]/10'
                          }`}
                          disabled={isSharedProfileView || (trade.remaining_position_pct || 100) <= 0 || (trade.position_status || 'active') === 'pending'}
                          title={(trade.position_status || 'active') === 'pending' ? "Mark position as ACTIVE before using Partial" : "Partially close position"}
                        >
                          <Percent size={16} weight="bold" className="mr-1.5" />
                          Partial
                        </Button>
                        {/* Hedge Button - Disabled for pending trades */}
                        <Button
                          onClick={(isSharedProfileView || (trade.position_status || 'active') === 'pending') ? undefined : (e) => { e.stopPropagation(); openHedgeModal(trade); }}
                          data-testid={`hedge-btn-${trade.id}`}
                          variant="outline"
                          className={`bg-transparent border-cyan-500/30 text-cyan-400 font-semibold px-3 ${
                            (isSharedProfileView || (trade.position_status || 'active') === 'pending') ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-500/10'
                          }`}
                          disabled={isSharedProfileView || (trade.position_status || 'active') === 'pending'}
                          title={(trade.position_status || 'active') === 'pending' ? "Mark position as ACTIVE before using Hedge" : "Add hedge position"}
                        >
                          <Shield size={16} weight={trade.has_hedge ? "fill" : "bold"} className="mr-1.5" />
                          Hedge
                        </Button>
                        {/* Close Button - Always available */}
                        <Button
                          onClick={isSharedProfileView ? undefined : (e) => { e.stopPropagation(); openClosePanel(trade); }}
                          data-testid={`close-btn-${trade.id}`}
                          className={`bg-white text-black font-bold px-5 ${
                            isSharedProfileView ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'
                          }`}
                          disabled={isSharedProfileView}
                        >
                          CLOSE
                        </Button>
                      </div>
                    )}
                    
                    {/* Stop Confirmation Inline */}
                    {stoppingTradeId === trade.id && (
                      <div 
                        className="flex items-center gap-2 bg-[#FF6B6B]/10 border border-[#FF6B6B]/20 rounded-sm px-3 py-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-sm text-white/80">Close at stop-loss?</span>
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleStopTrade(trade.id); }}
                          disabled={stoppingInProgress}
                          size="sm"
                          className="bg-[#FF6B6B] text-white font-semibold hover:bg-[#FF5252] px-3 h-7"
                          data-testid={`confirm-stop-btn-${trade.id}`}
                        >
                          {stoppingInProgress ? (
                            <ArrowsClockwise size={14} className="animate-spin" />
                          ) : (
                            "Yes"
                          )}
                        </Button>
                        <Button
                          onClick={(e) => { e.stopPropagation(); cancelStop(); }}
                          variant="ghost"
                          size="sm"
                          className="text-white/60 hover:text-white hover:bg-white/5 px-2 h-7"
                          data-testid={`cancel-stop-btn-${trade.id}`}
                        >
                          No
                        </Button>
                      </div>
                    )}
                    
                    {/* Status Row: Cancel + Timestamp (Hedge removed - now primary action) */}
                    {closingTradeId !== trade.id && stoppingTradeId !== trade.id && (
                      <div className="flex items-center gap-3 text-xs">
                        {/* Cancel Trade - disabled in shared profile mode */}
                        {isSharedProfileView ? (
                          <span
                            className="text-white/20 cursor-not-allowed"
                            title="View only"
                          >
                            Cancel trade
                          </span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); openCancelModal(trade.id); }}
                            data-testid={`cancel-trade-btn-${trade.id}`}
                            className="text-white/30 hover:text-red-400 transition-colors hover:underline"
                          >
                            Cancel trade
                          </button>
                        )}
                        
                        <span className="text-white/10">·</span>
                        
                        {/* Timestamp - Show activation time for active, created_at for pending */}
                        <span className="text-white/30 font-mono">
                          {(trade.position_status || 'active') === 'pending' ? (
                            <>
                              Created {trade.created_at ? format(new Date(trade.created_at), "MMM d, HH:mm") : 'N/A'}
                            </>
                          ) : (
                            <>
                              {trade.activated_at ? 'Active since' : 'Opened'} {(trade.activated_at || trade.created_at) ? format(new Date(trade.activated_at || trade.created_at), "MMM d, HH:mm") : 'N/A'}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Close Panel */}
                {closingTradeId === trade.id && (
                  <div 
                    className="border-t border-white/10 bg-[#0A0A0A] p-6" 
                    data-testid={`close-panel-${trade.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-end gap-4">
                      <div className="flex-1 max-w-xs">
                        <label className="text-white/60 text-xs font-mono mb-2 block">
                          CLOSING PRICE
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                          <Input
                            type="number"
                            step="any"
                            min="0"
                            value={closingPrice}
                            onChange={(e) => setClosingPrice(e.target.value)}
                            placeholder="Enter closing price"
                            data-testid={`closing-price-input-${trade.id}`}
                            className="bg-[#121212] border-white/10 text-white font-mono pl-7"
                            autoFocus
                          />
                        </div>
                      </div>

                      <Button
                        onClick={() => handleFetchMarketPrice(trade.asset)}
                        disabled={fetchingPrice}
                        variant="outline"
                        className="bg-transparent border-white/10 text-white hover:bg-white/5 gap-2"
                        data-testid={`market-price-btn-${trade.id}`}
                      >
                        {fetchingPrice ? (
                          <ArrowsClockwise size={16} className="animate-spin" />
                        ) : (
                          <CurrencyDollar size={16} />
                        )}
                        Use Market Price
                      </Button>

                      <Button
                        onClick={() => handleCloseTrade(trade.id)}
                        disabled={submitting || !closingPrice}
                        className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 gap-2 px-6"
                        data-testid={`confirm-close-btn-${trade.id}`}
                      >
                        {submitting ? (
                          <ArrowsClockwise size={16} className="animate-spin" />
                        ) : (
                          <Check size={16} weight="bold" />
                        )}
                        Confirm Close
                      </Button>

                      <Button
                        onClick={cancelClose}
                        variant="ghost"
                        className="text-white/60 hover:text-white hover:bg-white/5"
                        data-testid={`cancel-close-btn-${trade.id}`}
                      >
                        <X size={16} />
                      </Button>
                    </div>

                    {/* Preview calculation */}
                    {closingPrice && parseFloat(closingPrice) > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <div className="flex items-center gap-6 text-sm">
                          <div>
                            <span className="text-white/40 font-mono">OUTCOME: </span>
                            {(() => {
                              const cp = parseFloat(closingPrice);
                              const isWin = trade.direction === "long" 
                                ? cp > trade.entry 
                                : cp < trade.entry;
                              const isBreakeven = cp === trade.entry;
                              
                              if (isBreakeven) {
                                return <span className="text-white/60 font-bold">BREAKEVEN</span>;
                              }
                              return isWin 
                                ? <span className="text-[#00E676] font-bold">WIN</span>
                                : <span className="text-[#FF3B30] font-bold">LOSS</span>;
                            })()}
                          </div>
                          {trade.hand_size && (
                            <div>
                              <span className="text-white/40 font-mono">EST. P&L: </span>
                              {(() => {
                                const cp = parseFloat(closingPrice);
                                const entry = trade.entry;
                                const sl = trade.stop_loss;
                                const direction = trade.direction;
                                
                                let rr;
                                if (direction === "long") {
                                  const risk = entry - sl;
                                  rr = risk > 0 ? (cp - entry) / risk : 0;
                                } else {
                                  const risk = sl - entry;
                                  rr = risk > 0 ? (entry - cp) / risk : 0;
                                }
                                
                                const pnl = trade.hand_size * rr;
                                return (
                                  <span className={`font-bold font-mono ${pnl >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* OLD Partial Close Panel - REMOVED, replaced with modal */}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Partial Execution Modal (V1) */}
      <AlertDialog open={!!partialExecuteTrade} onOpenChange={(open) => { if (!open) closePartialExecuteModal(); }}>
        <AlertDialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="partial-execute-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white">
              <Percent size={24} weight="duotone" className="text-[#FFC107]" />
              Partial Execution
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-white/60 text-sm space-y-4 pt-2">
                <p>
                  Close a portion of your <span className="text-white font-semibold">{partialExecuteTrade?.asset}</span> position.
                  {partialExecuteTrade?.remaining_position_pct !== undefined && partialExecuteTrade?.remaining_position_pct < 100 && (
                    <span className="text-[#FFC107] ml-1">
                      (Currently {partialExecuteTrade.remaining_position_pct}% remaining)
                    </span>
                  )}
                </p>
                
                {/* Percentage Selection */}
                <div>
                  <label className="text-white/40 text-xs font-mono mb-2 block">SELECT PORTION TO CLOSE</label>
                  <div className="flex gap-2">
                    {[25, 50, 75].map((pct) => {
                      const remainingPct = partialExecuteTrade?.remaining_position_pct ?? 100;
                      const isDisabled = pct > remainingPct;
                      return (
                        <button
                          key={pct}
                          onClick={() => !isDisabled && setPartialPercentage(pct)}
                          disabled={isDisabled}
                          className={`flex-1 py-3 rounded-sm font-bold text-lg transition-all ${
                            partialPercentage === pct
                              ? 'bg-[#FFC107] text-black'
                              : isDisabled
                                ? 'bg-white/5 text-white/20 cursor-not-allowed'
                                : 'bg-white/10 text-white hover:bg-white/15'
                          }`}
                          data-testid={`partial-pct-${pct}`}
                        >
                          {pct}%
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Market Price Input */}
                <div>
                  <label className="text-white/40 text-xs font-mono mb-2 block">MARKET PRICE</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={partialMarketPrice}
                        onChange={(e) => setPartialMarketPrice(e.target.value)}
                        placeholder="Enter market price"
                        data-testid="partial-market-price-input"
                        className="bg-[#0A0A0A] border-white/10 text-white font-mono pl-7"
                      />
                    </div>
                    <Button
                      onClick={() => partialExecuteTrade && handleFetchPartialPrice(partialExecuteTrade.asset)}
                      disabled={fetchingPartialPrice}
                      variant="outline"
                      className="bg-transparent border-white/10 text-white hover:bg-white/5"
                      data-testid="fetch-partial-price-btn"
                    >
                      {fetchingPartialPrice ? (
                        <ArrowsClockwise size={16} className="animate-spin" />
                      ) : (
                        <CurrencyDollar size={16} />
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Preview Calculation */}
                {partialPercentage && partialMarketPrice && parseFloat(partialMarketPrice) > 0 && partialExecuteTrade && (
                  <div className="bg-white/5 rounded-sm p-3 border border-white/10">
                    <div className="text-white/40 text-xs font-mono mb-2">PREVIEW</div>
                    {(() => {
                      const mp = parseFloat(partialMarketPrice);
                      const entry = partialExecuteTrade.entry;
                      const sl = partialExecuteTrade.stop_loss;
                      const direction = partialExecuteTrade.direction;
                      const handSize = partialExecuteTrade.hand_size || 0;
                      
                      const riskPerUnit = Math.abs(entry - sl);
                      const priceDiff = direction === 'long' ? mp - entry : entry - mp;
                      const partialRR = riskPerUnit > 0 ? priceDiff / riskPerUnit : 0;
                      const partialHandSize = handSize * (partialPercentage / 100);
                      const estPnl = partialHandSize * partialRR;
                      
                      const isProfit = estPnl >= 0;
                      
                      return (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white/60 text-sm">Est. Realized P&L: </span>
                            <span className={`font-bold font-mono ${isProfit ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                              {isProfit ? '+' : ''}${estPnl.toFixed(2)}
                            </span>
                          </div>
                          <div className="text-white/40 text-xs">
                            RR: {partialRR.toFixed(2)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-4">
            <AlertDialogCancel 
              onClick={closePartialExecuteModal}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              data-testid="partial-execute-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePartialExecute}
              disabled={partialExecuting || !partialPercentage || !partialMarketPrice || parseFloat(partialMarketPrice) <= 0}
              className="bg-[#FFC107] text-black font-bold hover:bg-[#FFC107]/90"
              data-testid="partial-execute-confirm"
            >
              {partialExecuting ? (
                <ArrowsClockwise size={16} className="animate-spin mr-2" />
              ) : null}
              Execute {partialPercentage ? `${partialPercentage}%` : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trade Details Drawer */}
      {selectedTrade && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 z-40 transition-opacity"
            onClick={closeTradeDrawer}
          />
          
          {/* Drawer */}
          <div 
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#121212] border-l border-white/10 z-50 shadow-2xl overflow-y-auto transition-transform"
            data-testid="trade-details-drawer"
          >
            {/* Drawer Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black font-heading">{selectedTrade.asset}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    selectedTrade.direction === "long" 
                      ? "bg-[#00E676]/10 text-[#00E676]" 
                      : "bg-[#FF3B30]/10 text-[#FF3B30]"
                  }`}>
                    {selectedTrade.direction?.toUpperCase()}
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    selectedTrade.trade_type === 'swing_trade'
                      ? "bg-purple-500/10 text-purple-400"
                      : selectedTrade.trade_type === 'scalp'
                      ? "bg-yellow-500/10 text-yellow-400"
                      : "bg-blue-500/10 text-blue-400"
                  }`}>
                    {selectedTrade.trade_type === 'swing_trade' ? 'Swing' : selectedTrade.trade_type === 'scalp' ? 'Scalp' : 'Day'}
                  </span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#FFC107]/10 text-[#FFC107]/80">
                    {selectedTrade.setup_name || 'No setup'}
                  </span>
                </div>
              </div>
              <button
                onClick={closeTradeDrawer}
                className="p-2 hover:bg-white/5 rounded-sm transition-colors"
                data-testid="close-drawer-btn"
              >
                <XIcon size={20} className="text-white/60" />
              </button>
            </div>

            {/* Trade Info */}
            <div className="p-4 border-b border-white/10">
              <h4 className="text-white/40 text-xs font-mono mb-3">TRADE INFO</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-white/40 text-xs">Entry</span>
                  <p className="text-white font-mono">${selectedTrade.entry?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-white/40 text-xs">Stop Loss</span>
                  <p className="text-[#FF3B30] font-mono">${selectedTrade.stop_loss?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-white/40 text-xs">Take Profit</span>
                  <p className="text-[#00E676] font-mono">
                    {selectedTrade.take_profit ? `$${selectedTrade.take_profit.toLocaleString()}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-white/40 text-xs">Risk/Reward</span>
                  <p className="text-white font-mono font-bold">{selectedTrade.rr?.toFixed(2) || 'N/A'}</p>
                </div>
                {selectedTrade.timeframe && (
                  <div>
                    <span className="text-white/40 text-xs">Timeframe</span>
                    <p className="text-white font-mono">{selectedTrade.timeframe}</p>
                  </div>
                )}
                {/* Show different info based on position status */}
                {(selectedTrade.position_status || 'active') === 'pending' ? (
                  <>
                    <div>
                      <span className="text-white/40 text-xs">Created</span>
                      <p className="text-white/70 text-sm">
                        {selectedTrade.created_at ? format(new Date(selectedTrade.created_at), "MMM d, yyyy HH:mm") : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-white/40 text-xs">Status</span>
                      <p className="text-[#F59E0B] text-sm font-medium">Waiting for entry zone</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <span className="text-white/40 text-xs">Active Since</span>
                    <p className="text-white/70 text-sm">
                      {(selectedTrade.activated_at || selectedTrade.created_at) ? format(new Date(selectedTrade.activated_at || selectedTrade.created_at), "MMM d, yyyy HH:mm") : 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Setup Info */}
            {selectedTrade.setup_name && (
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/40 text-xs font-mono mb-2">SETUP</h4>
                <p className="text-white font-medium">{selectedTrade.setup_name}</p>
              </div>
            )}

            {/* Trade Chart Image - Shown in both private and public mode */}
            {selectedTrade.image_data && (
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/40 text-xs font-mono mb-3">CHART</h4>
                <div className="border border-white/10 rounded-sm overflow-hidden" data-testid="trade-chart-image">
                  <img
                    src={`data:image/png;base64,${selectedTrade.image_data}`}
                    alt="Trading chart"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            )}

            {/* Partial Execution Info - Show if any partials executed */}
            {selectedTrade.remaining_position_pct !== undefined && selectedTrade.remaining_position_pct < 100 && (
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/40 text-xs font-mono mb-3 flex items-center gap-2">
                  <Percent size={14} className="text-[#FFC107]" />
                  PARTIAL EXECUTIONS
                </h4>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <span className="text-white/40 text-xs">Remaining Position</span>
                    <p className="text-[#FFC107] font-mono font-bold">{selectedTrade.remaining_position_pct}%</p>
                  </div>
                  <div>
                    <span className="text-white/40 text-xs">Realized P&L</span>
                    <p className={`font-mono font-bold ${(selectedTrade.realized_pnl || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                      {(selectedTrade.realized_pnl || 0) >= 0 ? '+' : ''}${(selectedTrade.realized_pnl || 0).toFixed(2)}
                    </p>
                  </div>
                  {/* Realized RR in drawer */}
                  <div data-testid="drawer-realized-rr">
                    <span className="text-white/40 text-xs">Realized RR</span>
                    {(() => {
                      const realizedUsd = selectedTrade.realized_pnl || 0;
                      const hedgeVal = selectedTrade.hedge_value || 0;
                      const handSize = selectedTrade.hand_size || 0;
                      if (handSize === 0) return <p className="text-white/40 font-mono font-bold">—</p>;
                      const realizedRR = (realizedUsd + hedgeVal) / handSize;
                      const isPositive = realizedRR > 0;
                      const isZero = realizedRR === 0;
                      return (
                        <p className={`font-mono font-bold ${
                          isZero 
                            ? 'text-white/40' 
                            : isPositive 
                              ? 'text-[#00E676]' 
                              : 'text-[#FF3B30]'
                        }`}>
                          {isPositive ? '+' : ''}{realizedRR.toFixed(2)}R
                        </p>
                      );
                    })()}
                  </div>
                </div>
                {/* Partial Execution History */}
                {selectedTrade.partial_executions && selectedTrade.partial_executions.length > 0 && (
                  <div className="space-y-2">
                    {selectedTrade.partial_executions.map((exec, idx) => (
                      <div key={idx} className="bg-white/5 rounded-sm p-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-white/60">{exec.percentage}% @ ${exec.market_price?.toLocaleString()}</span>
                          <span className={`font-mono ${exec.profit_usd >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                            {exec.profit_usd >= 0 ? '+' : ''}${exec.profit_usd?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes Section */}
            <div className="p-4">
              <h4 className="text-white/40 text-xs font-mono mb-3 flex items-center gap-2">
                <Note size={14} />
                NOTES
              </h4>
              {isSharedProfileView ? (
                // Read-only notes view for public/shared profile mode
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 min-h-[100px]">
                  {selectedTrade.notes ? (
                    <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                  ) : (
                    <p className="text-white/40 text-sm italic">No notes added</p>
                  )}
                </div>
              ) : (
                // Editable notes for private mode
                <>
                  <Textarea
                    value={drawerNote}
                    onChange={(e) => setDrawerNote(e.target.value)}
                    placeholder="Write your thoughts about this trade..."
                    className="bg-[#0A0A0A] border-white/10 text-white min-h-[150px] resize-none focus:border-white/20"
                    data-testid="trade-notes-textarea"
                  />
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={handleSaveNote}
                      disabled={savingNote || drawerNote === (selectedTrade.notes || "")}
                      className="bg-white text-black font-bold hover:bg-gray-200"
                      data-testid="save-notes-btn"
                    >
                      {savingNote ? (
                        <>
                          <ArrowsClockwise size={16} className="animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check size={16} className="mr-2" />
                          Save Notes
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Cancel Trade Confirmation Dialog */}
      <AlertDialog open={!!cancelTradeId} onOpenChange={(open) => {
        if (!open) {
          closeCancelModal();
        }
      }}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading flex items-center gap-2">
              <Prohibit size={20} className="text-red-400" />
              Mark Trade as Not Triggered
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This trade will be moved to History as a planned trade that was not executed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Cancel Reason Selection */}
          <div className="py-4">
            <label className="text-white/60 text-sm mb-3 block">
              Why are you canceling this trade? <span className="text-[#FF3B30]">*</span>
            </label>
            <div className="space-y-2">
              {cancelReasonOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                    cancelReason === option.value 
                      ? 'border-[#FFA726] bg-[#FFA726]/10' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={option.value}
                    checked={cancelReason === option.value}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    cancelReason === option.value 
                      ? 'border-[#FFA726]' 
                      : 'border-white/30'
                  }`}>
                    {cancelReason === option.value && (
                      <div className="w-2 h-2 rounded-full bg-[#FFA726]" />
                    )}
                  </div>
                  <span className={`text-sm ${cancelReason === option.value ? 'text-white' : 'text-white/70'}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-dialog-cancel-btn"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              onClick={() => setCancelReason('')}
            >
              No, Keep Trade
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cancel-dialog-confirm-btn"
              onClick={() => handleCancelTrade(cancelTradeId, cancelReason)}
              disabled={!cancelReason}
              className={`font-bold ${
                cancelReason 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              Yes, Cancel Trade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hedge Modal */}
      <AlertDialog open={!!hedgeTrade} onOpenChange={(open) => { if (!open) closeHedgeModal(); }}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading flex items-center gap-2">
              <Shield size={20} weight="duotone" className="text-cyan-400" />
              Add Hedge
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Record a hedge result for <span className="text-white font-semibold">{hedgeTrade?.asset}</span>.
              You can add multiple hedges - each will be accumulated into the total.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Current Total (if has existing hedges) */}
            {hedgeTrade?.has_hedge && (
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-cyan-400/60 text-xs font-mono uppercase">Current Hedge Total</div>
                    <div className={`text-xl font-mono font-bold ${
                      (hedgeTrade?.hedge_value || 0) >= 0 ? 'text-cyan-400' : 'text-cyan-400'
                    }`}>
                      {(hedgeTrade?.hedge_value || 0) >= 0 ? '+' : ''}${(hedgeTrade?.hedge_value || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/40 text-xs">{hedgeTrade?.hedge_count || 1} hedge{(hedgeTrade?.hedge_count || 1) > 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Previous Hedge Entries (if any) */}
            {hedgeTrade?.hedge_entries && hedgeTrade.hedge_entries.length > 0 && (
              <div className="space-y-2">
                <div className="text-white/40 text-xs font-mono uppercase">Previous Hedges</div>
                <div className="max-h-32 overflow-y-auto space-y-1.5 bg-[#0A0A0A] rounded-sm p-2">
                  {hedgeTrade.hedge_entries.map((entry, idx) => (
                    <div 
                      key={entry.id || idx} 
                      className="flex items-center justify-between text-sm p-2 bg-white/5 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`font-mono font-semibold ${
                          (entry.value || 0) >= 0 ? 'text-cyan-400' : 'text-cyan-400'
                        }`}>
                          {(entry.value || 0) >= 0 ? '+' : ''}${(entry.value || 0).toFixed(2)}
                        </span>
                        {entry.note && (
                          <span className="text-white/40 text-xs truncate max-w-[150px]" title={entry.note}>
                            {entry.note}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveSingleHedge(entry.id)}
                        className="text-white/30 hover:text-red-400 transition-colors p-1"
                        title="Remove this hedge"
                        data-testid={`remove-hedge-entry-${entry.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hedge Value Input */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">
                New Hedge Result (USD) <span className="text-[#FF3B30]">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={hedgeValue}
                  onChange={(e) => setHedgeValue(e.target.value)}
                  placeholder="e.g., 45 or -20"
                  className="bg-[#0A0A0A] border-white/10 text-white pl-7 font-mono"
                  data-testid="hedge-value-input"
                />
              </div>
              <p className="text-white/40 text-xs mt-1.5">
                Positive = hedge helped • Negative = hedge hurt
              </p>
            </div>
            
            {/* Hedge Note (optional) */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">
                Note (optional)
              </label>
              <Textarea
                value={hedgeNote}
                onChange={(e) => setHedgeNote(e.target.value)}
                placeholder="e.g., Sold call option at 1.5x premium"
                className="bg-[#0A0A0A] border-white/10 text-white resize-none h-16"
                data-testid="hedge-note-input"
              />
            </div>
          </div>
          
          <AlertDialogFooter className="flex gap-2">
            {/* Remove All Hedges Button (only shown if trade has hedge) */}
            {hedgeTrade?.has_hedge && (
              <Button
                variant="ghost"
                onClick={handleRemoveHedge}
                disabled={hedgeSubmitting}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mr-auto"
                data-testid="remove-all-hedges-btn"
              >
                <Trash size={16} className="mr-1.5" />
                Remove All
              </Button>
            )}
            <AlertDialogCancel
              data-testid="hedge-cancel-btn"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleSaveHedge}
              disabled={hedgeSubmitting || !hedgeValue}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold"
              data-testid="hedge-save-btn"
            >
              {hedgeSubmitting ? (
                <ArrowsClockwise size={16} className="animate-spin mr-1.5" />
              ) : (
                <Shield size={16} className="mr-1.5" />
              )}
              Add Hedge
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Adjust Stop Modal */}
      <AlertDialog open={!!adjustStopTrade} onOpenChange={(open) => { if (!open) closeAdjustStopModal(); }}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading flex items-center gap-2">
              <Target size={20} weight="duotone" className="text-[#FF3B30]" />
              Adjust Stop Loss
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Update the stop loss for <span className="text-white font-semibold">{adjustStopTrade?.asset}</span>.
              <span className="block mt-1 text-xs text-white/40">
                Official RR remains based on your initial stop/risk.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Current Stop Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">INITIAL STOP</div>
                <div className="text-white/70 font-mono font-semibold">
                  ${(adjustStopTrade?.initial_stop_loss || adjustStopTrade?.stop_loss)?.toLocaleString()}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">CURRENT STOP</div>
                <div className="text-[#FF3B30] font-mono font-semibold">
                  ${adjustStopTrade?.stop_loss?.toLocaleString()}
                </div>
              </div>
            </div>
            
            {/* Entry Reference */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-3 flex items-center justify-between">
              <div className="text-white/40 text-xs font-mono">ENTRY PRICE</div>
              <div className="text-white font-mono font-semibold">
                ${adjustStopTrade?.entry?.toLocaleString()}
              </div>
            </div>
            
            {/* New Stop Input */}
            <div>
              <label className="block text-white/70 text-sm mb-2 font-medium">
                New Stop Loss
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <Input
                  type="number"
                  step="any"
                  value={newStopLoss}
                  onChange={(e) => setNewStopLoss(e.target.value)}
                  placeholder="Enter new stop price"
                  className="bg-[#0A0A0A] border-white/10 text-white pl-7 font-mono"
                  data-testid="adjust-stop-input"
                />
              </div>
              <p className="text-white/30 text-xs mt-1">
                Enter any price. Move above entry to lock profit, at entry for break-even.
              </p>
            </div>
            
            {/* Risk Preview */}
            {(() => {
              const preview = calculateRiskPreview(adjustStopTrade, newStopLoss);
              if (!preview) return null;
              
              // Determine the styling based on stop type
              let containerClass, statusText, statusColor;
              if (preview.isProfitLocking) {
                containerClass = 'bg-[#00E676]/10 border-[#00E676]/20';
                statusText = 'Profit locked - guaranteed gain if stop hit';
                statusColor = 'text-[#00E676]/80';
              } else if (preview.isBreakEven) {
                containerClass = 'bg-[#00B8D4]/10 border-[#00B8D4]/20';
                statusText = 'Break-even stop - no loss if hit';
                statusColor = 'text-[#00B8D4]/80';
              } else if (preview.riskReduced) {
                containerClass = 'bg-[#00E676]/10 border-[#00E676]/20';
                statusText = 'Risk reduced - protecting capital';
                statusColor = 'text-[#00E676]/80';
              } else {
                containerClass = 'bg-[#FFC107]/10 border-[#FFC107]/20';
                statusText = 'Risk increased - wider stop';
                statusColor = 'text-[#FFC107]/80';
              }
              
              return (
                <div className={`p-3 rounded-sm border ${containerClass}`}>
                  <div className="text-white/60 text-xs font-mono uppercase mb-2 flex items-center gap-2">
                    {preview.isProfitLocking ? 'Profit Lock Preview' : preview.isBreakEven ? 'Break-Even Preview' : 'Risk Preview'}
                    {preview.remainingPct < 100 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">
                        {preview.remainingPct}% position remaining
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <div className="text-white/40 text-[10px]">Initial Risk</div>
                      <div className="font-mono text-white">${preview.initialRisk.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px]">
                        {preview.isProfitLocking ? 'Locked Profit' : preview.isBreakEven ? 'At Risk' : 'Current Risk'}
                      </div>
                      <div className={`font-mono ${preview.isProfitLocking ? 'text-[#00E676]' : preview.isBreakEven ? 'text-[#00B8D4]' : 'text-white'}`}>
                        {preview.isProfitLocking ? '+' : ''}${Math.abs(preview.currentRisk).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px]">Change</div>
                      <div className={`font-mono font-semibold ${
                        preview.riskReduced ? 'text-[#00E676]' : 'text-[#FFC107]'
                      }`}>
                        {preview.riskReduced ? '' : '+'}{preview.riskChange.toFixed(2)}
                        <span className="text-[10px] ml-1">({preview.percentChange}%)</span>
                      </div>
                    </div>
                  </div>
                  <div className={`mt-2 text-xs ${statusColor}`}>
                    {statusText}
                  </div>
                </div>
              );
            })()}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="adjust-stop-cancel-btn"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleSaveAdjustStop}
              disabled={adjustStopSubmitting || !newStopLoss}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white font-bold"
              data-testid="adjust-stop-save-btn"
            >
              {adjustStopSubmitting ? (
                <ArrowsClockwise size={16} className="animate-spin mr-1.5" />
              ) : (
                <Check size={16} className="mr-1.5" />
              )}
              Save Stop
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Share Positions Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Share Open Positions</DialogTitle>
            <DialogDescription className="text-white/60">
              Share a read-only view of your current open positions with others.
            </DialogDescription>
          </DialogHeader>
          
          {loadingShareSettings ? (
            <div className="flex justify-center py-8">
              <GeniLoader size="sm" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-white/10 rounded-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                    <Eye size={20} className="text-white/60" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Enable position sharing</p>
                    <p className="text-white/40 text-xs">
                      {positionsSharingEnabled ? 'Anyone with the link can view' : 'Link is currently private'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={positionsSharingEnabled}
                  onCheckedChange={togglePositionSharing}
                  disabled={savingShareSettings}
                  data-testid="positions-sharing-toggle"
                />
              </div>
              
              {/* Share Link */}
              {positionsSharingEnabled && (
                <div className="space-y-3">
                  <label className="text-white/60 text-sm">Your Positions Link</label>
                  {sharingUsername ? (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 font-mono text-sm text-white/80 truncate">
                          {window.location.origin}/positions/{sharingUsername}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-white/10 text-white hover:bg-white/5"
                          onClick={copyShareLink}
                          data-testid="copy-positions-link-btn"
                        >
                          <Copy size={16} className="mr-2" />
                          Copy Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-white/10 text-white hover:bg-white/5"
                          onClick={openShareLink}
                          data-testid="open-positions-link-btn"
                        >
                          <LinkSimple size={16} className="mr-2" />
                          Open
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-[#0A0A0A] border border-[#FF9500]/30 rounded-sm">
                      <p className="text-[#FF9500] text-sm mb-2">Username required</p>
                      <p className="text-white/60 text-xs mb-3">
                        Set a username in Settings to get your shareable link.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-white hover:bg-white/5"
                        onClick={() => {
                          setShowShareModal(false);
                          navigate('/settings');
                        }}
                      >
                        Go to Settings
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Info */}
              <div className="text-white/40 text-xs space-y-1">
                <p>• Positions update in real-time as you open/close trades</p>
                <p>• Private notes are never shared</p>
                <p>• You can disable sharing at any time</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
